import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { Switch } from "@aurumiq/ui/components/switch";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { relativeTime } from "@aurumiq/ui/lib/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, RefreshCw, Save, Send } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { PageHeader } from "@/app/page-header";
import { SelectField, TextField } from "@/components/field";
import { QueryState, Stat, StatList } from "@/components/query-state";
import { STAFF_ROLES } from "@/features/staff/queries";
import { useApplySsoSettings, useSaveSsoSettings, useSsoSettings } from "@/features/system/queries";
import type { SsoSettings } from "@/lib/types";

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const schema = z.object({
  enabled: z.boolean(),
  tenant_id: z
    .string()
    .trim()
    .max(64)
    .refine((v) => !v || GUID.test(v) || /^[a-z0-9.-]+$/i.test(v), "GUID or tenant domain"),
  client_id: z
    .string()
    .trim()
    .refine((v) => !v || GUID.test(v), "Must be a GUID like 1a2b3c4d-… (the secret goes below)"),
  client_secret: z
    .string()
    .trim()
    .max(500)
    .refine((v) => !GUID.test(v), "That is the Secret ID; paste the secret Value"),
  secret_expires_on: z.string(),
  allowed_domains: z.string().max(500),
  default_role: z.enum(STAFF_ROLES),
});
type FormValues = z.infer<typeof schema>;

const toForm = (s: SsoSettings): FormValues => ({
  enabled: s.enabled,
  tenant_id: s.tenant_id,
  client_id: s.client_id,
  client_secret: "",
  secret_expires_on: s.secret_expires_on ?? "",
  allowed_domains: s.allowed_domains.join(", "),
  default_role: (STAFF_ROLES as readonly string[]).includes(s.default_role)
    ? (s.default_role as (typeof STAFF_ROLES)[number])
    : "pending",
});

export function SsoSettingsPage() {
  const q = useSsoSettings();
  return (
    <>
      <PageHeader
        title="Single sign-on"
        description="Let staff sign in with their Microsoft work account. The auth service completes the Microsoft login with the client secret; email + password sign-in stays available."
      />
      <QueryState query={q}>{(s) => <SsoForm initial={s} />}</QueryState>
    </>
  );
}

function CopyLine({ value }: { value: string }) {
  return (
    <div className="flex items-center gap-2 font-mono text-xs">
      <span className="truncate">{value}</span>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        aria-label={`Copy ${value}`}
        onClick={() => navigator.clipboard?.writeText(value)}
      >
        <Copy className="size-3.5" />
      </button>
    </div>
  );
}

function SsoForm({ initial }: { initial: SsoSettings }) {
  const save = useSaveSsoSettings();
  const apply = useApplySsoSettings();
  const form = useForm<FormValues>({ resolver: zodResolver(schema), values: toForm(initial) });
  const st = initial.status;
  const onSubmit = form.handleSubmit((v) =>
    save.mutate({
      enabled: v.enabled,
      tenant_id: v.tenant_id,
      client_id: v.client_id,
      client_secret: v.client_secret || null,
      secret_expires_on: v.secret_expires_on || null,
      allowed_domains: v.allowed_domains
        .split(/[,\s]+/)
        .map((d) => d.trim())
        .filter(Boolean),
      default_role: v.default_role,
    }),
  );
  const live = st.provider_live === true && st.applied && initial.enabled;
  const canApply =
    st.problems.length === 0 && st.apply_mode !== "disabled" && !form.formState.isDirty;
  const expiry = st.secret_expires_in_days;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
      <form onSubmit={onSubmit} className="grid gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Microsoft Entra ID (Azure)</CardTitle>
            <Controller
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <div className="flex items-center gap-2 text-sm">
                  <Switch
                    id="sso-enabled"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-label="Enable Microsoft sign-in"
                  />
                  <label htmlFor="sso-enabled">{field.value ? "Enabled" : "Disabled"}</label>
                </div>
              )}
            />
          </CardHeader>
          <CardContent className="grid gap-3">
            <TextField
              label="Directory (tenant) ID"
              placeholder="00000000-0000-0000-0000-000000000000"
              hint="Entra admin centre → app registration → Overview. Blank = any Microsoft tenant (“common”); set it so only your organisation can sign in."
              error={form.formState.errors.tenant_id?.message}
              {...form.register("tenant_id")}
            />
            <TextField
              label="Application (client) ID"
              placeholder="00000000-0000-0000-0000-000000000000"
              error={form.formState.errors.client_id?.message}
              {...form.register("client_id")}
            />
            <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
              <TextField
                label="Client secret"
                type="password"
                autoComplete="new-password"
                placeholder={
                  initial.has_secret
                    ? "•••••••• (stored; paste to replace)"
                    : "Secret Value from Certificates & secrets"
                }
                hint="Stored encrypted with the server master key and only ever sent to the auth service."
                error={form.formState.errors.client_secret?.message}
                {...form.register("client_secret")}
              />
              <TextField
                label="Secret expires on"
                type="date"
                hint="Optional reminder"
                error={form.formState.errors.secret_expires_on?.message}
                {...form.register("secret_expires_on")}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
              <SelectField
                label="Default role for new sign-ins"
                hint={
                  form.watch("default_role") === "pending"
                    ? "No access until a superadmin assigns a role on the Staff page."
                    : form.watch("default_role") === "superadmin"
                      ? "Every Microsoft account admitted gets full access. Restrict domains below."
                      : "Applies to accounts created by their first Microsoft sign-in."
                }
                error={form.formState.errors.default_role?.message}
                {...form.register("default_role")}
              >
                {STAFF_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </SelectField>
              <TextField
                label="Allowed sign-in domains"
                placeholder="paravai.tech, example.com"
                hint="Optional, comma-separated. Accounts from other domains are refused even if the tenant admits them."
                error={form.formState.errors.allowed_domains?.message}
                {...form.register("allowed_domains")}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <p className="text-muted-foreground text-xs">
                {initial.updated_at
                  ? `Saved ${relativeTime(initial.updated_at)} by ${initial.updated_by ?? "—"}`
                  : "Never saved."}
              </p>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  variant={form.formState.isDirty ? "default" : "outline"}
                  disabled={save.isPending || !form.formState.isDirty}
                >
                  <Save /> {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button
                  type="button"
                  disabled={!canApply || apply.isPending}
                  onClick={() => apply.mutate()}
                  title={
                    form.formState.isDirty
                      ? "Save first"
                      : (st.problems[0] ?? "Write the credentials to the auth service")
                  }
                >
                  <Send /> {apply.isPending ? "Applying…" : "Apply to auth service"}
                </Button>
              </div>
            </div>
            {st.problems.length ? (
              <ul className="list-disc rounded-md bg-warning-bg px-3 py-2 pl-7 text-sm text-warning">
                {st.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      </form>

      <div className="grid content-start gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatList>
              <Stat label="Sign in with Microsoft">
                {live ? (
                  <StatusTag variant="ok">LIVE</StatusTag>
                ) : initial.enabled && st.applied ? (
                  <StatusTag variant="warn">
                    <RefreshCw className="mr-1 size-3" /> RESTARTING
                  </StatusTag>
                ) : (
                  <StatusTag>OFF</StatusTag>
                )}
              </Stat>
              <Stat label="Credentials applied">
                {st.applied
                  ? `yes${st.applied_at ? ` · ${relativeTime(st.applied_at)}` : ""}`
                  : "not yet"}
              </Stat>
              <Stat label="Auth service reports provider">
                {st.provider_live === null ? "unknown" : st.provider_live ? "on" : "off"}
              </Stat>
              <Stat label="Apply mode">
                {st.apply_mode === "watcher"
                  ? "automatic restart"
                  : st.apply_mode === "manual"
                    ? "manual restart"
                    : "not configured on this server"}
              </Stat>
              <Stat label="Secret expiry">
                {expiry === null ? (
                  "—"
                ) : expiry < 0 ? (
                  <StatusTag variant="err">EXPIRED</StatusTag>
                ) : expiry < 30 ? (
                  <StatusTag variant="warn">{expiry} DAYS</StatusTag>
                ) : (
                  `${expiry} days`
                )}
              </Stat>
            </StatList>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Set-up in Entra (one time)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal space-y-2 pl-4">
              <li>
                Entra admin centre → <i>App registrations</i> → <b>New registration</b> (e.g.
                “AurumIQ Admin”). Accounts in this organisational directory only.
              </li>
              <li>
                Platform <b>Web</b>, redirect URI:
                <CopyLine value={st.redirect_uri} />
              </li>
              <li>
                <i>Certificates &amp; secrets</i> → new client secret → copy the <b>Value</b> (not
                the Secret ID) into the form; note the expiry date.
              </li>
              <li>
                <i>API permissions</i>: Microsoft Graph → <code>openid</code>, <code>email</code>,{" "}
                <code>profile</code> (delegated) and grant admin consent.
              </li>
              <li>
                Paste the Directory ID and Application ID, switch on, <b>Save</b>, then{" "}
                <b>Apply to auth service</b>. The auth service restarts (about a minute).
              </li>
            </ol>
            <p className="text-muted-foreground text-xs">
              First-time Microsoft sign-ins are created as <i>pending</i> staff with no permissions
              until a superadmin assigns a role on the Staff page. Customer logins on the auth
              service are unavailable during the restart.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
